/**
 * @file serviceFactory.js
 * @description Factory para proveer servicios compartidos evitando dependencias circulares
 * Implementa el patrón Singleton y Factory para mejor modularidad y testabilidad
 * @version 1.0.0
 */

import { CorsProxyService } from './CorsProxyService';
import CorsProxyInterceptorSlim from './CorsProxyInterceptorSlim';


/**
 * Factory que centraliza la creación e inyección de servicios para toda la aplicación
 * Evita dependencias circulares y facilita testing mediante la inversión de control
 */
class ServiceFactory {
  constructor() {
    this.services = new Map();
    this._initializeServices();
  }
  
  /**
   * Inicializa los servicios principales usando lazy loading
   * para evitar dependencias circulares
   * @private
   */
  _initializeServices() {
    // Registrar el servicio de proxy CORS
    if (!this.services.has('corsProxy')) {
      this.services.set('corsProxy', new CorsProxyService());
      console.log('[ServiceFactory] CorsProxyService inicializado');
    }
  }
  
  /**
   * Inicializa el interceptor con referencia al servicio proxy.
   * Utiliza importación estática de `CorsProxyInterceptorSlim`, evitando
   * promesas y garantizando disponibilidad inmediata y sincrónica.
   * @private
   */
  _initializeInterceptor() {
    if (!this.services.has('corsInterceptor')) {
      try {
        // Crear instancia pasando el servicio proxy como dependencia
        const corsProxy = this.getService('corsProxy');
        const interceptor = new CorsProxyInterceptorSlim(corsProxy);
        this.services.set('corsInterceptor', interceptor);
        console.log('[ServiceFactory] CorsProxyInterceptor inicializado');
        return interceptor;
      } catch (error) {
        console.error('[ServiceFactory] Error al inicializar interceptor:', error);
        return null;
      }
    }
    return this.services.get('corsInterceptor');
  }
  
  /**
   * Obtiene una instancia de servicio por su nombre
   * @param {string} serviceName - Nombre del servicio
   * @returns {Object} - Instancia del servicio o promesa si requiere inicialización
   */
  getService(serviceName) {
    // Servicios que requieren inicialización especial asíncrona
    if (serviceName === 'corsInterceptor') {
      if (!this.services.has('corsInterceptor')) {
        return this._initializeInterceptor();
      }
    }
    
    if (!this.services.has(serviceName)) {
      console.error(`[ServiceFactory] Servicio '${serviceName}' no disponible`);
      return null;
    }
    return this.services.get(serviceName);
  }
  
  /**
   * Obtiene todos los servicios disponibles
   * @returns {Object} - Mapa de servicios disponibles
   */
  getServices() {
    const result = {};
    this.services.forEach((service, name) => {
      result[name] = service;
    });
    return result;
  }
  
  /**
   * Getter para el servicio de proxy CORS
   * @returns {CorsProxyService}
   */
  get corsProxyService() {
    return this.getService('corsProxy');
  }
  
  /**
   * Getter para el interceptor de proxy CORS (nombre canónico)
   * @returns {CorsProxyInterceptor}
   */
  get corsProxyInterceptor() {
    return this.getService('corsInterceptor');
  }

  /**
   * Getter alias para compatibilidad retro-compatible
   * Muchos módulos legacy aún esperan `serviceFactory.corsInterceptor`.
   * Delegamos al nuevo nombre `corsProxyInterceptor`.
   * @deprecated se mantendrá hasta eliminar referencias antiguas
   */
  get corsInterceptor() {
    return this.corsProxyInterceptor;
  }
}

// Singleton para uso en toda la aplicación
const serviceFactory = new ServiceFactory();

export default serviceFactory;
